import { alpha, Box, Button, paletteColors, useMediaQuery } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import FileDownloadOutlinedIcon from '@evaneos/b2b-ui/icons/FileDownloadOutlined';

import { MessageAttachment } from '../../../../../resources/discussion/types';

type PhotoCardProps = {
    attachment: MessageAttachment;
    onDownloadLinkClick: () => void;
    onOpen: () => void;
};

/**
 * Accessible card pattern:
 * - Primary action (open modal): transparent button covering the whole card via position:absolute + inset:0
 * - Secondary action (download): comes later in DOM so naturally paints on top, no z-index needed
 * Both actions are reachable by mouse and keyboard without nesting interactive elements.
 */
export const PhotoCard = ({ attachment, onDownloadLinkClick, onOpen }: PhotoCardProps) => {
    const supportsHover = useMediaQuery('(hover: hover)');
    const { formatMessage } = useIntl();

    return (
        <Box
            component="li"
            data-spec-id="attachment-photo"
            sx={{
                position: 'relative',
                width: 150,
                height: 150,
                borderRadius: 2,
                overflow: 'hidden',
                listStyle: 'none',
                flexShrink: 0,
                '&:hover [data-photo-hover-download-button], &:focus-within [data-photo-hover-download-button]':
                    { opacity: 1 },
                '&:hover [data-photo-hover-overlay], &:focus-within [data-photo-hover-overlay]': {
                    background: alpha(paletteColors.neutral[900]!, 0.3),
                },
            }}
        >
            <Box
                component="img"
                src={attachment.link}
                alt={attachment.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />

            {/* Primary action: covers the whole card, doubles as hover overlay */}
            <Box
                component="button"
                data-photo-hover-overlay
                onClick={onOpen}
                aria-label={attachment.name}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 150ms',
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '-2px',
                    },
                }}
            />

            {/* Secondary action: download — later in DOM so naturally sits on top */}
            <Button
                data-photo-hover-download-button
                component="a"
                href={attachment.link}
                variant="outlined"
                aria-label={formatMessage(
                    { id: 'discussion.attachments_list.download_aria_label' },
                    { name: attachment.name },
                )}
                onClick={onDownloadLinkClick}
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: supportsHover ? 0 : 1,
                    minWidth: 0,
                    p: 1,
                    transition: 'opacity 150ms',
                }}
            >
                <FileDownloadOutlinedIcon />
            </Button>
        </Box>
    );
};
